import { useMemo, useState } from 'react';
import { Heart, User, Users, UsersRound, type LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import type { ExperienceOption } from '@flyingmuse/shared';

/* ------------------------------------------------------------------ */
/* Decompose options into a "group size" axis and a "session" axis     */
/* ------------------------------------------------------------------ */

interface SessionItem {
  durationLabel: string | null;
  option: ExperienceOption;
}
interface VariantGroup {
  label: string;
  items: SessionItem[];
}
interface Decomposed {
  axisLabel: string;
  variants: VariantGroup[];
}

function formatDuration(min: number | null): string | null {
  if (!min) return null;
  if (min % 60 === 0) {
    const h = min / 60;
    return `${h} Hour${h > 1 ? 's' : ''}`;
  }
  return `${min} min`;
}

const tokenize = (s: string): string[] => s.trim().split(/\s+/).filter(Boolean);
const GROUPISH = /solo|couple|group|family|people|person|private/i;

/** Match a variant label to an audience icon, mirroring the experience cards. */
function iconFor(label: string): ComponentType<LucideProps> | null {
  if (/couple/i.test(label)) return Heart;
  if (/family/i.test(label)) return UsersRound;
  if (/group|people|person/i.test(label)) return Users;
  if (/solo|private/i.test(label)) return User;
  return null;
}

/**
 * Split each option title ("Couples Casual Santorini Photoshoot | 1 Hour") into
 * a short variant label ("Couples") and a session label ("1 Hour"), stripping
 * the words every option shares. Falls back to full titles when they don't fit
 * the grid (e.g. distinct named packages).
 */
function decompose(options: ExperienceOption[]): Decomposed {
  const parsed = options.map((option) => {
    const [leftRaw, rightRaw] = option.title.split('|');
    return {
      left: (leftRaw ?? option.title).trim(),
      durationLabel: rightRaw?.trim() || formatDuration(option.durationMinutes),
      option,
    };
  });

  const lists = parsed.map((p) => tokenize(p.left));
  let pre = 0;
  let suf = 0;
  if (lists.length > 1) {
    const minLen = Math.min(...lists.map((l) => l.length));
    while (
      pre < minLen &&
      lists.every((l) => l[pre].toLowerCase() === lists[0][pre].toLowerCase())
    )
      pre++;
    while (
      suf < minLen - pre &&
      lists.every(
        (l) =>
          l[l.length - 1 - suf].toLowerCase() ===
          lists[0][lists[0].length - 1 - suf].toLowerCase(),
      )
    )
      suf++;
  }

  const variants: VariantGroup[] = [];
  parsed.forEach((p, i) => {
    const t = lists[i];
    const label = t.slice(pre, t.length - suf).join(' ').trim() || p.left;
    let group = variants.find((v) => v.label === label);
    if (!group) {
      group = { label, items: [] };
      variants.push(group);
    }
    group.items.push({ durationLabel: p.durationLabel, option: p.option });
  });

  for (const v of variants) {
    v.items.sort(
      (a, b) => (a.option.durationMinutes ?? 0) - (b.option.durationMinutes ?? 0),
    );
  }

  const axisLabel = variants.every((v) => GROUPISH.test(v.label))
    ? 'Group size'
    : 'Package';
  return { axisLabel, variants };
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const pill = (active: boolean) =>
  `rounded-full border px-5 py-2.5 font-sans text-sm font-medium transition-colors ${
    active
      ? 'border-crimson bg-crimson text-white'
      : 'border-charcoal/15 bg-white text-charcoal hover:border-crimson'
  }`;

export function OptionStep({
  options,
  selectedOptionId,
  onSelect,
}: {
  options: ExperienceOption[];
  selectedOptionId: number | null;
  onSelect: (optionId: number) => void;
}) {
  const { axisLabel, variants } = useMemo(() => decompose(options), [options]);
  const singleVariant = variants.length === 1;

  const initialVariant = useMemo(() => {
    if (selectedOptionId != null) {
      const idx = variants.findIndex((v) =>
        v.items.some((it) => it.option.id === selectedOptionId),
      );
      if (idx >= 0) return idx;
    }
    return singleVariant ? 0 : null;
  }, [variants, selectedOptionId, singleVariant]);

  const [variantIdx, setVariantIdx] = useState<number | null>(initialVariant);
  const active = variantIdx != null ? variants[variantIdx] : null;

  function pickVariant(i: number) {
    const v = variants[i];
    // Variants with a single session (e.g. Family) resolve immediately.
    if (v.items.length === 1) {
      onSelect(v.items[0].option.id);
      return;
    }
    setVariantIdx(i);
  }

  return (
    <div>
      <h3 className="font-display text-2xl font-medium text-charcoal">
        Choose your session
      </h3>
      <p className="mt-1.5 font-sans text-sm font-light text-stone">
        Pick the package that suits you. You can fine-tune the date and guests next.
      </p>

      {/* Group size / package axis */}
      {!singleVariant && (
        <div className="mt-6">
          <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
            {axisLabel}
          </p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {variants.map((v, i) => {
              const Icon = iconFor(v.label);
              return (
                <button
                  key={v.label}
                  type="button"
                  onClick={() => pickVariant(i)}
                  className={`inline-flex items-center gap-2 ${pill(variantIdx === i)}`}
                >
                  {Icon && <Icon className="h-4 w-4" strokeWidth={1.7} />}
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Session-length axis */}
      {active && active.items.length > 1 && (
        <div className="mt-7">
          <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
            Session length
          </p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {active.items.map((it) => (
              <button
                key={it.option.id}
                type="button"
                onClick={() => onSelect(it.option.id)}
                className={pill(it.option.id === selectedOptionId)}
              >
                {it.durationLabel ?? it.option.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
